import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '@/hooks/useTelegram';

const threatTypes = [
  {
    name: 'Амбициозные организации',
    impulse: 'Захватить власть и территорию',
    examples: ['Культ', 'Гильдия воров', 'Империя'],
    moves: [
      'Атаковать открыто, с силой',
      'Атаковать тайно, из-за угла',
      'Защитить важный актив',
      'Показать силу',
      'Предложить сделку',
    ],
  },
  {
    name: 'Планарные силы',
    impulse: 'Открыть порталы, призвать существ',
    examples: ['Демоны', 'Элементали', 'Феи'],
    moves: [
      'Исказить реальность',
      'Создать искушение',
      'Предложить сделку с последствиями',
      'Открыть портал',
      'Показать своё истинное лицо',
    ],
  },
  {
    name: 'Враги цивилизации',
    impulse: 'Разрушить порядок',
    examples: ['Варварские племена', 'Восстание крестьян', 'Анархисты'],
    moves: [
      'Разрушить символ власти',
      'Поджечь, сломать, разорвать',
      'Собрать недовольных',
      'Напасть на слабых',
      'Потребовать дань',
    ],
  },
  {
    name: 'Нежить',
    impulse: 'Распространять смерть и разложение',
    examples: ['Личи', 'Армии мертвецов', 'Призраки'],
    moves: [
      'Поднять мёртвых',
      'Распространить болезнь',
      'Осквернить святое место',
      'Истощить жизненную силу',
      'Показать ужасы смерти',
    ],
  },
  {
    name: 'Орды',
    impulse: 'Поглотить всё на своём пути',
    examples: ['Гоблины', 'Орки', 'Крысы'],
    moves: [
      'Захлестнуть числом',
      'Окружить и отрезать',
      'Показать жестокость',
      'Разграбить всё ценное',
      'Вынудить к бегству',
    ],
  },
];

export function FrontsPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();

  return (
    <div className="min-h-screen bg-gray-900 safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="sticky top-0 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-10">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => {
              hapticFeedback('light');
              navigate('/gm-tools');
            }}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Назад
          </button>
          <h1 className="text-lg font-bold text-white">Фронты</h1>
          <div className="w-16" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        {/* What is a Front */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <h2 className="text-lg font-bold text-amber-400 mb-3">Что такое фронт?</h2>
          <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50">
            <p className="text-gray-300 text-sm mb-3">
              Фронт — это набор связанных угроз, которые движутся к своей цели независимо от действий игроков.
            </p>
            <p className="text-gray-300 text-sm mb-3">
              Каждый фронт имеет:
            </p>
            <ul className="text-gray-300 text-sm space-y-1">
              <li>• <span className="text-amber-400">Угрозы</span> — конкретные опасности</li>
              <li>• <span className="text-amber-400">Мрачные предзнаменования</span> — что случится, если герои не вмешаются</li>
              <li>• <span className="text-amber-400">Рок</span> — финальная катастрофа</li>
              <li>• <span className="text-amber-400">Ставки</span> — что на кону</li>
            </ul>
          </div>
        </motion.section>

        {/* Threat Types */}
        <section>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-lg font-bold text-amber-400 mb-4"
          >
            Типы угроз
          </motion.h2>

          <div className="space-y-4">
            {threatTypes.map((threat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + 0.05 * index }}
                className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50"
              >
                <h3 className="font-bold text-white mb-2">{threat.name}</h3>
                <p className="text-amber-400 text-sm mb-3">
                  Импульс: {threat.impulse}
                </p>

                <div className="mb-3">
                  <p className="text-gray-400 text-xs mb-1">Примеры:</p>
                  <div className="flex flex-wrap gap-1">
                    {threat.examples.map((ex, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs rounded bg-gray-700 text-gray-300">
                        {ex}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-gray-400 text-xs mb-1">Ходы угрозы:</p>
                  <ul className="space-y-1">
                    {threat.moves.map((move, i) => (
                      <li key={i} className="text-gray-300 text-sm">• {move}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Creating a Front */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <h2 className="text-lg font-bold text-amber-400 mb-3">Как создать фронт?</h2>
          <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 space-y-3">
            <p className="text-gray-300 text-sm">
              1. <span className="text-white">Выберите тему</span> — о чём этот фронт?
            </p>
            <p className="text-gray-300 text-sm">
              2. <span className="text-white">Создайте 2-3 угрозы</span> — кто или что угрожает?
            </p>
            <p className="text-gray-300 text-sm">
              3. <span className="text-white">Напишите мрачные предзнаменования</span> — 3-5 шагов к катастрофе
            </p>
            <p className="text-gray-300 text-sm">
              4. <span className="text-white">Определите рок</span> — что случится в конце?
            </p>
            <p className="text-gray-300 text-sm">
              5. <span className="text-white">Задайте ставки</span> — что на кону для игроков?
            </p>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
